import { ThemeConfig } from '../../branding/theme';

export interface Organization {
    id: string;
    name: string;
    theme?: ThemeConfig;
    hpAccountId: string;
}

export enum Role {
    SUPER_USER = 'su',
    ADMIN = 'a',
    RECRUITER = 'r',
}

export interface User {
    firstName: string;
    lastName: string;
    email: string;
    userProfileRef: string;
    role: {
        roleType: Role;
        _id: string;
    };
}

// TODO: use common enum INTERVIEW_PARTICIPANT_TYPE in other places instead of PARTICIPANT_TYPE.
export enum INTERVIEW_PARTICIPANT_TYPE {
    VIRTUAL_CALENDAR = 'VirtualCalendar',
    INTERVIEWER = 'Interviewer',
}

// TODO: Align other places with this common type and remove the redundant type
// To check and remove - client/src/HarverPages/interviewHistory/service.ts
export enum CancelledByRoles {
    recruiter = 'recruiter',
    candidate = 'candidate',
}

export enum Status {
    pending = 'pending',
    scheduled = 'scheduled',
    unavailable = 'unavailable',
    cancelled = 'cancelled',
    completed = 'completed',
    expired = 'expired',
}

export enum Locales {
    en_US = 'en_US',
    fr_FR = 'fr_FR',
    nl_NL = 'nl_NL',
    de_DE = 'de_DE',
}

enum RequiredNumberOfInterviewers {
    oneInterviewer = 1,
    allInterviewers = 'all',
}

export enum LOCATION {
    onSite = 'ON_SITE',
    phoneCall = 'PHONE_CALL',
    harverLiveVideo = 'HARVER_LIVE_VIDEO',
    custom = 'CUSTOM',
    teams = 'TEAMS',
}

interface CancelDetails {
    by: CancelledByRoles;
    at: Date;
    reason: string;
    message?: string;
    isReschedule?: boolean;
    context: 'cancelledBefore' | 'cancelledAfter' | 'rescheduled';
}

interface BreakBetweenInterviews {
    before: number;
    after: number;
}

interface Candidate {
    email: string;
    firstName: string;
    lastName: string;
    preferredLocale: Locales;
}

interface CalendarEvent {
    id: string;
    title: string;
}

interface RequestedTimePeriod {
    duration: number;
    withinDays: number;
    timePeriod: {
        // todo: remove the start field. It will be calculated on the fly
        start: string;
        end: string;
    };
}

type Interviewer = {
    firstName: string;
    lastName: string;
    email: string;
    cronofyAccountId: string;
    primaryCalendarId: string;
    isAttendee?: boolean;
};

type VirtualInterviewer = {
    name: string;
    virtualCalendarId: string;
    cronofyAccountId: string;
    primaryCalendarId: string;
    isAttendee?: boolean;
    owner?: string;
};

type OptionalInterviewer = {
    email: string;
    firstName?: string;
    lastName?: string;
};

export interface ReassignedBy {
    firstName: string;
    lastName: string;
}

export interface ReassignmentParticipant {
    firstName?: string;
    lastName?: string;
    name?: string;
    type: INTERVIEW_PARTICIPANT_TYPE;
}

export interface Reassignment {
    original: ReassignmentParticipant;
    reAssignee: ReassignmentParticipant;
    date: Date;
    by: ReassignedBy;
}

export interface InterviewDetails {
    interviewId: string;
    settingsId: string;
    settingsName?: string;
    recruiterPreferredTimezone: string;
    requiredNumberOfInterviewers: RequiredNumberOfInterviewers;
    calendarEvent: CalendarEvent;
    candidate: Candidate;
    requestedTimePeriod: RequestedTimePeriod;
    scheduleId: string;
    requestedBy: string;
    schedulingLink: string;
    interviewers: Interviewer[];
    optionalInterviewers: OptionalInterviewer[];
    location: string;
    organizationId: string;
    organizationName: string;
    hpAccountId: string;
    smartInviteId?: string;
    virtualCalendarSmartInviteId?: string;
    atsParams?: {
        [key: string]: unknown;
    };
    callbackUrl?: string;
    recruiterCancellationToken: string;
    break: BreakBetweenInterviews;
    noOfReschedules: number;
    candidateVideoLink?: string;
    recruiterVideoLink?: string;
    virtualCalendars: VirtualInterviewer[];
    interviewInstructions?: string;
    createdAt: Date;
    updatedAt: Date;
    scheduledAt: Date;
    hasRescheduled: boolean;
    reassignments?: Reassignment[];
}

export interface InitializedInterview extends InterviewDetails {
    status: Status.pending;
    startTime: string;
}

export interface ScheduledInterview extends InterviewDetails {
    status: Status.scheduled;
    startTime: string;
    endTime: string;
    candidatePreferredTimezone: string;
    scheduledAt: Date;
}

export interface CandidateUnavailable extends InterviewDetails {
    status: Status.unavailable;
    candidateMessage?: string;
    unavailableReason?: string;
    markedUnavailableAt: Date;
    startTime: string;
}

export interface CancelledInterview extends InterviewDetails {
    status: Status.cancelled;
    cancelDetails: CancelDetails;
    startTime?: string;
}

export interface CompletedInterview extends Omit<ScheduledInterview, 'status'> {
    status: Status.completed;
    completedAt: Date;
}

export interface ExpiredInterview extends Omit<InitializedInterview, 'status'> {
    status: Status.expired;
    expiredAt: Date;
}

export enum VirtualCalendarStatus {
    active = 'active',
    disabled = 'disabled',
}
export interface VirtualCalendar {
    virtualCalendarId: string;
    name: string;
    status: VirtualCalendarStatus;
}
export interface InterviewSetting {
    settingsId: string;
    isEnabled: boolean;
    settingsName: string;
    templateId: string;
    organizationId: string;
    interviewInstructions: string;
    interviewers: Interviewer[];
    virtualCalendars: VirtualCalendar[];
    optionalInterviewers?: OptionalInterviewer[] | [];
    openInvite?: {
        isEnabled: boolean;
        linkId: string;
        preferredLocale: string;
    };
    isArchived?: boolean;
}

export type Interview =
    | InitializedInterview
    | ScheduledInterview
    | CandidateUnavailable
    | CancelledInterview
    | CompletedInterview
    | ExpiredInterview;

export type ContextType = {
    id?: string;
    email?: string;
    accountId?: string;
    role?: string;
};
